/**
 * @file Copies simulation state onto the scene graph.
 *
 * This is the ONLY system that touches Three.js objects belonging to entities,
 * and it contains no game rules whatsoever (GDD §25): it attaches meshes for
 * new entities, mirrors transforms, and returns meshes to their pool when an
 * entity dies.
 */

import { System } from '../ecs/System.js';
import { Transform, Renderable, Velocity, AIBrain, RangedAI } from '../components/index.js';
import { Events } from '../core/EventBus.js';

export class RenderSystem extends System {
  static priority = 100;

  constructor() {
    super();
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
  }

  /** @returns {void} */
  init() {
    this._subscriptions.push(
      this.ctx.events.on(Events.ENTITY_DESTROYED, (entity) => this._detach(entity))
    );
  }

  /**
   * @param {number} _dt Delta time in seconds.
   * @returns {void}
   */
  update(_dt) {
    const { scene } = this.ctx;

    for (const entity of this.world.query(Transform, Renderable)) {
      const renderable = entity.get(Renderable);

      if (!renderable.attached) {
        scene.add(renderable.object);
        renderable.attached = true;
      }
      if (renderable.isStatic) continue;

      const transform = entity.get(Transform);
      const object = renderable.object;

      const stunned = isStunned(entity);
      const yOffset = stunned ? renderable.proneYOffset : renderable.yOffset;
      object.position.set(transform.position.x, transform.position.y + yOffset, transform.position.z);

      if (renderable.faceVelocity) {
        const velocity = entity.get(Velocity);
        if (velocity && velocity.linear.lengthSq() > 1e-6) {
          object.rotation.y = Math.atan2(velocity.linear.x, velocity.linear.z);
        }
      } else {
        object.rotation.y = transform.rotationY;
      }
      object.rotation.x = stunned ? Math.PI / 2 : 0;
      object.rotation.z = 0;

      const scale = transform.scale;
      const brain = entity.get(AIBrain);
      if (brain?.meleeSwingRemaining > 0 && brain.meleeSwingDuration > 0) {
        const t = 1 - brain.meleeSwingRemaining / brain.meleeSwingDuration;
        const pulse = Math.sin(Math.min(1, Math.max(0, t)) * Math.PI);
        object.scale.set(scale * (1 + pulse * 0.12), scale * (1 - pulse * 0.06), scale * (1 + pulse * 0.26));
        object.position.x += Math.sin(object.rotation.y) * pulse * 0.22;
        object.position.z += Math.cos(object.rotation.y) * pulse * 0.22;
      } else {
        object.scale.set(scale, scale, scale);
      }
    }
  }

  /**
   * Returns an entity's mesh to its prefab pool.
   * @param {import('../ecs/Entity.js').Entity} entity Entity being destroyed.
   * @returns {void}
   * @private
   */
  _detach(entity) {
    const renderable = entity.get(Renderable);
    if (!renderable) return;

    renderable.object.removeFromParent();
    renderable.attached = false;
    this.ctx.meshFactory.release(renderable.prefabKey, /** @type {any} */ (renderable.object));
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
  }
}

/**
 * @param {import('../ecs/Entity.js').Entity} entity Entity being rendered.
 * @returns {boolean} Whether its AI is currently stunned.
 */
function isStunned(entity) {
  return (
    (entity.get(AIBrain)?.stunRemaining ?? 0) > 0 ||
    (entity.get(RangedAI)?.stunRemaining ?? 0) > 0
  );
}
